import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, IconButton, makeStyles } from '@material-ui/core';
import { Block, Heading, Tooltip, Typography } from '@klp/design';
import { Loader } from '@klp/design/Loader';
import type { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables';
import { useGetBrukereQuery } from '../services/rtkApi';
import { Main } from '@klp/layout';
import { filterSearchResult, Search } from '@klp/search';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { UserRoutes } from '../config';
import { useHistory } from 'react-router-dom';

import { useCurrentUser } from '@klp/auth';
import { PersistentFilterTable } from '@klp/design/PersistentFilter/PersistentFilterTable';

const useStyles = makeStyles(() => ({
    container: {},
    roleTypography: {
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    rolesColumn: {
        maxWidth: '200px',
    },
}));

export default function Brukerliste() {
    const { t } = useTranslation();
    const classes = useStyles();
    const { currentUser } = useCurrentUser();

    const [searchResult, setSearchResult] = useState('');
    const handleSearch = (searchString: string) => {
        setSearchResult(searchString);
    };
    const history = useHistory();

    const hasEditAccess =
        currentUser?.isBrukerAdministrator || currentUser?.isEDAdmin;

    const {
        data: brukereData,
        isLoading: brukereIsLoading,
        isFetching: brukereIsFetching,
    } = useGetBrukereQuery();

    const [filteredBrukere, setFilteredBrukere] = useState(
        () => brukereData ?? [],
    );

    useEffect(() => {
        const filtered = filterSearchResult(brukereData ?? [], searchResult, [
            'fulltnavn',
            'epost',
        ]);
        setFilteredBrukere(filtered);
    }, [searchResult, brukereData]);

    const tableColumns: Array<MUIDataTableColumn> = [
        {
            label: t('users.fullname'),
            name: 'fulltnavn',
            options: {
                customBodyRender: (value: string | undefined) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {value?.trim()}
                    </Typography>
                ),
            },
        },
        {
            label: t('users.email'),
            name: 'epost',
            options: {
                filter: false,
                customBodyRender: (value: string | undefined) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {value}
                    </Typography>
                ),
            },
        },
        {
            label: t('users.region'),
            name: 'region',
            options: {
                customBodyRender: (value: string | undefined) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {value}
                    </Typography>
                ),
            },
        },
        {
            label: t('users.roles'),
            name: 'roller',
            options: {
                filter: true,
                filterType: 'multiselect',
                customBodyRender: (value) => {
                    if (!value) return null;
                    const rolesArray = Array.isArray(value) ? value : [value];

                    const roleNames = rolesArray.map((role) => {
                        const roleName = t(
                            `users.rolesSelector.${role.rolle || role}`,
                        );
                        const region = role.region ? ` (${role.region})` : '';
                        return `${roleName}${region}`;
                    });

                    return (
                        <Typography
                            klpTypographyVariant="small"
                            klpColor="main"
                            className={classes.rolesColumn}
                        >
                            {roleNames.join(', ')}
                        </Typography>
                    );
                },
                filterOptions: {
                    names:
                        brukereData && Array.isArray(brukereData)
                            ? Array.from(
                                  new Set(
                                      brukereData
                                          .flatMap((user) =>
                                              user.roller.map((role) => {
                                                  const roleName = t(
                                                      `users.rolesSelector.${role.rolle || role}`,
                                                  );
                                                  const region = role.region
                                                      ? ` (${role.region})`
                                                      : '';
                                                  return `${roleName}${region}`;
                                              }),
                                          )
                                          .filter(Boolean),
                                  ),
                              )
                            : [],
                    logic: (userRoles, filters) => {
                        if (filters.length === 0) return true;
                        const rolesArray = Array.isArray(userRoles)
                            ? userRoles
                            : [userRoles];
                        const userRoleNames = rolesArray.map((role) => {
                            const roleName = t(
                                `users.rolesSelector.${role.rolle || role}`,
                            );
                            const region = role.region
                                ? ` (${role.region})`
                                : '';
                            return `${roleName}${region}`;
                        });
                        return !filters.some((filter) =>
                            userRoleNames.includes(filter),
                        );
                    },
                },
            },
        },
        {
            label: t('users.action.label'),
            name: 'action',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex) => {
                    const userEmail = filteredBrukere?.[dataIndex]?.epost;
                    const route = UserRoutes.single(String(dataIndex));

                    return hasEditAccess ? (
                        <Tooltip translation="users.editUser.title" title="">
                            <IconButton
                                size="medium"
                                onClick={() =>
                                    history.push(route, { user: userEmail })
                                }
                            >
                                <Illustration
                                    icon={IconName.EDIT}
                                    size="small"
                                    klpPalette="primary"
                                />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Tooltip
                            translation="users.showUserInfo.title"
                            title=""
                        >
                            <IconButton
                                size="medium"
                                onClick={() =>
                                    history.push(route, { user: userEmail })
                                }
                            >
                                <Illustration
                                    icon={IconName.SEARCH}
                                    size="small"
                                    klpPalette="primary"
                                />
                            </IconButton>
                        </Tooltip>
                    );
                },
            },
        },
    ];

    const tableOptions: MUIDataTableOptions = {
        search: true,
        download: false,
        print: false,
        viewColumns: true,
        filter: true,
        sort: true,
        sortOrder: {
            name: 'gyldigFra',
            direction: 'desc',
        },
        selectableRowsHeader: false,
        selectableRowsHideCheckboxes: true,
        filterType: 'multiselect',
        customToolbarSelect: () => <></>,
        textLabels: {
            body: {
                noMatch: t('users.noUsers'),
                toolTip: t('tables.body.toolTip'),
                columnHeaderTooltip: (column) =>
                    t('tables.body.toolTipFor') + column.label,
            },
            pagination: {
                next: t('tables.pagination.next'),
                previous: t('tables.pagination.previous'),
                rowsPerPage: t('tables.pagination.rowsPerPage'),
                displayRows: t('tables.pagination.displayRows'),
            },
            filter: {
                all: t('tables.filter.all'),
                title: t('tables.filter.title'),
                reset: t('tables.filter.reset'),
            },
            toolbar: {
                search: t('tables.toolbar.search'),
                downloadCsv: t('tables.toolbar.downloadCsv'),
                print: t('tables.toolbar.print'),
                viewColumns: t('tables.toolbar.viewColumns'),
                filterTable: t('tables.toolbar.filterTable'),
            },
            viewColumns: {
                title: t('tables.viewColumns.title'),
                titleAria: t('tables.viewColumns.titleAria'),
            },
        },
        setFilterChipProps: () => {
            return {
                color: 'secondary',
                variant: 'outlined',
            };
        },
    };

    return (
        <Main>
            <Loader isLoading={brukereIsLoading || brukereIsFetching}>
                <Block paddingBottom="fat">
                    <Heading klpHeadingLevel="THREE" klpColor="dark">
                        {t('users.title')}
                    </Heading>
                </Block>
                <Grid container={true} direction="column" spacing={3}>
                    <Grid
                        container={true}
                        direction="column"
                        justifyContent="flex-start"
                    >
                        <Block
                            paddingTop="tiny"
                            paddingLeft="compact"
                            paddingBottom="compact"
                        >
                            <Search
                                searchResult={searchResult}
                                searchHandler={handleSearch}
                                searchOnType={true}
                                placeholder="users.searchField"
                            />
                        </Block>
                        <Block paddingTop="tiny" paddingLeft="compact">
                            <Loader isLoading={brukereIsLoading}>
                                <PersistentFilterTable
                                    title=""
                                    data={filteredBrukere ?? []}
                                    columns={tableColumns}
                                    options={{
                                        ...tableOptions,
                                        setRowProps: (row, dataIndex) => ({
                                            style: {
                                                backgroundColor:
                                                    dataIndex % 2 === 0
                                                        ? '#f5f5f5'
                                                        : '#ffffff', // Annenhver hvite og grå rader
                                                border: '1px solid #e0e0e0',
                                                boxShadow:
                                                    '0px 4px 8px rgba(0, 0, 0, 0.1)',
                                            },
                                        }),
                                    }}
                                    tableKey="userAdministration"
                                />
                            </Loader>
                        </Block>
                    </Grid>
                </Grid>
            </Loader>
        </Main>
    );
}
